import { useWidget } from "../../../providers/widget"
import { ExploreWidgetConfig } from "../config"
import { useExplorer } from "../providers"
import Stylesheet from "../../../components/Stylesheet"
import WebFont from "../../../components/WebFont"
import ClientOnly from "../../../components/ClientOnly"
import WidgetSection from "../../../components/WidgetSection"
import PropertyGrid from "./PropertyGrid"
import PropertySlider from "./PropertySlider"
import FilterBar from "./filters/FilterBar"
import { isHeroBar, SecondaryRow, useFilterTotalsState } from "./filters/searchShared"
import { lazy, Suspense, useEffect, useMemo, useRef } from "react"
import { useExplorerCss } from "../hooks/useExplorerCss"
import { getCardSkinTitleGoogleFont } from "../../../common/property-card/card-skin-css"
import { useFetchProperties, withBrowsePriceField } from "../data"
import MapShowTab from "./map/MapShowTab"
import { MobileResultsContext } from "./mobileResults/context"
import { useMobileResultsActive } from "./mobileResults/useMobileResultsActive"
import { useMobileResultsSheet } from "./mobileResults/useMobileResultsSheet"
import { SheetFab, SheetFilterPanel, SheetHandle, SheetTopbar } from "./mobileResults/MobileResultsChrome"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { CompareDock } from "../compare/CompareUi"

const ExplorerMap = lazy(() => import("./map/ExplorerMap"))

export function ExplorerContent() {
  const { options, resolvedTheme, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setMobileSheetActive } = useExplorer()
  const cssString = useExplorerCss()
  // #1219: the card skin's title preset (card-skin-css TITLE_FONTS) is a CSS
  // font-family value only — nothing requested it from Google Fonts, so a
  // customer site that hadn't already loaded it silently fell through to the
  // stack's system fallback. Webfont-load the ONE Google family the preset
  // needs; WebFont dedupes it against the main widget font itself.
  const titleGoogleFont = getCardSkinTitleGoogleFont(options.card?.titleFontFamily)
  const containerRef = useRef<HTMLDivElement>(null)

  // Hero (#934): the bar IS the widget. Map, grid, and the map restore tab are
  // out by construction (the toolkit hero renders none of them), whatever the
  // grid/map settings say — a hero instance flipping showMap must not grow a
  // sidebar it has no results to pair with.
  const hero = isHeroBar(options)
  const mapPlacement = options.map?.placement ?? 'right'
  const mapClasses = !hero && options.showMap
    ? `with-map ${state.mapVisible ? 'map-visible' : 'map-hidden'} map-${mapPlacement}`
    : 'without-map'
  // sticky-filter drives ONLY the legacy whole-block pin (and the Duda
  // shim's injected top-offset override keys on it too) — the pro bar owns
  // its stickyFilter behavior through the refine sticky anatomy (#1092), so
  // emitting the class there would be a lie every future `.sticky-filter`
  // rule trips over. The explorer.css rule is additionally scoped
  // :not(.hfse-sbp) as belt-and-braces.
  const stickyFilterClass = options.stickyFilter && options.searchbar?.variant !== 'pro' ? ' sticky-filter' : ''
  const heroClass = hero ? ' hfse-explorer-hero' : ''

  // Section title only renders once we have at least one property to show.
  // Filters go through withBrowsePriceField so this probe shares one cache entry with
  // <PropertyGrid /> / <PropertySlider /> and the SSR prefetch (no extra fetch).
  const platformUuid = options.filter?.platform
  const { data: propertiesData } = useFetchProperties(feedId, withBrowsePriceField(state.visitorFilters, options), options.filter.limit, platformUuid)
  const hasContent = (propertiesData?.pages?.[0]?.result?.length ?? 0) > 0

  // Mobile results sheet (#856): a client-only LAYOUT variant, gated on the
  // setting, the map feature, a results grid to sheet, the viewport, and the
  // one-sheet-per-document arm-guard. Hero has no results to sheet. The
  // hook runs in both layouts (inert when off) and the tree below keeps the
  // SAME React positions in both, so the grid is never remounted by the
  // switch — every slot that differs renders `false` on the other side.
  const mobileResults = options.mobileResults
  const sheetActive = useMobileResultsActive(
    mobileResults?.enabled === true && options.showMap === true && options.showGrid !== false && !hero,
    mobileResults?.breakpointPx ?? 768,
  )
  const totals = useFilterTotalsState()
  const sheet = useMobileResultsSheet({
    active: sheetActive && !!mobileResults,
    containerRef,
    settings: {
      navOffsetPx: mobileResults?.navOffsetPx ?? 0,
      initialSnap: mobileResults?.initialSnap ?? 'half',
      showToggle: mobileResults?.showToggle ?? true,
    },
    count: totals,
  })
  const onSheet = sheet.active

  // Publish the sheet's arm state so every bounds consumer suspends with it
  // (#856 review). The sheet renders the map WITHOUT changing the visitor's
  // `mapVisible`, so with `openMapOnMount` a rotation into the sheet mid-search
  // left the grid filtered by the bounds captured before the rotation, with the
  // search-as-move checkbox and the "Search this area" pill both CSS-hidden and
  // moveend inert — panning did nothing and there was no control to clear it.
  useEffect(() => {
    setMobileSheetActive(onSheet)
  }, [onSheet, setMobileSheetActive])
  const pro = options.searchbar?.variant === 'pro'
  // The sheet is map-first BY CONSTRUCTION and never writes the visitor's
  // list-layout map choice (`state.mapVisible` — toggleMap() would persist a
  // 72h "map open" pref just for loading the phone layout; codex r1 HIGH).
  const showMapBlock = onSheet || (!hero && options.showMap && state.mapVisible)

  // Stable child ELEMENTS: the sheet's snap state lives in this component,
  // so every drag settle re-renders it. The map, grid and filter bar read
  // everything from context, and handing React the same element reference
  // lets it skip their subtrees — otherwise each snap re-rendered LeafletMap,
  // whose per-render `properties` array re-ran the marker effect (rebuild +
  // re-frame on every snap; measured 24 marker mutations per settle).
  // #1251: on the sheet the results header is mounted ONCE, in the sheet head
  // (SheetHandle below). The bar rendered its own copy too and the extra was
  // suppressed with display:none — which still ran SortDropdown's canvas
  // measurement against an unrendered element and kept a second aria-live
  // count region re-rendering on every totals change.
  const filtersNode = useMemo(
    () => ((hero || options.showFilter) ? <FilterBar withSecondaryRow={!onSheet} /> : null),
    [hero, options.showFilter, onSheet],
  )
  const gridNode = useMemo(() => <PropertyGrid />, [])
  const sliderNode = useMemo(() => <PropertySlider />, [])
  const mapBlock = useMemo(() => (
    <div className="hfse-sidebar">
      {/* ExplorerMap is a lazy() chunk; rendering <Suspense> during SSR
          (legacy renderToString) leaves the boundary unfinished on the
          server → React #419 on hydrate (#771). Gate behind ClientOnly so
          no lazy/Suspense exists on the server or the first client render;
          the map loads one tick after mount. The hfse-sidebar div stays on
          the server to reserve the grid column and avoid layout shift. */}
      <ClientOnly>
        <Suspense fallback={<div className="hfse-map-container"><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Loading map...</div></div>}>
          <ExplorerMap />
        </Suspense>
      </ClientOnly>
    </div>
  ), [])

  const rootClass = onSheet
    ? `hfse hfse-explorer-container ${resolvedTheme === 'dark' ? 'dark' : ''} with-map map-visible map-${mapPlacement} ${sheet.rootClass} ${pro ? 'hfse-mres-pro' : 'hfse-mres-legacy'}`
    : `hfse hfse-explorer-container ${resolvedTheme === 'dark' ? 'dark' : ''} ${mapClasses}${stickyFilterClass}${heroClass}`

  return (
    <MobileResultsContext.Provider value={sheet.ctx}>
      <div
        ref={containerRef}
        className={rootClass}
        style={{ ...resolveWidgetSpacingStyle(options), ...sheet.rootStyle }}
        data-hfse-mres={onSheet ? sheet.snap : undefined}
        suppressHydrationWarning
      >
        <WebFont extraFamily={titleGoogleFont} />
        <Stylesheet css={cssString} />
        {!onSheet && hasContent && (
          <WidgetSection show={options.section?.show} title={options.section?.title} />
        )}

        {showMapBlock && mapBlock}

        {/* Sheet-only chrome: the fixed search CTA (SBP's trigger, or the
            legacy pill) and, for the legacy bar, its full-screen panel. */}
        {onSheet && filtersNode && <SheetTopbar sheet={sheet} pro={pro} filters={filtersNode} />}
        {onSheet && filtersNode && !pro && <SheetFilterPanel sheet={sheet} filters={filtersNode} />}

        {/* The body: the list column, or — on the sheet — the draggable,
            scrolling sheet itself. Same element, same children positions. */}
        <div ref={sheet.sheetRef} className={`hfse-body${onSheet ? ` ${sheet.sheetClass}` : ''}`} aria-label={onSheet ? 'Results' : undefined}>
          {/* Hero forces the bar on: it is the widget's entire content, so a
              stale showFilter=false must not render an empty shell. */}
          {!onSheet && filtersNode}
          {/* #1276: filtersNode is null exactly when the bar is hidden (search-results
              keyword / properties-showcase preset) and this isn't the hero layout —
              the count/sort/map controls (SecondaryRow) still need somewhere to
              render, reusing the exact component the bar and the mobile sheet
              header (below) already mount. */}
          {!onSheet && !filtersNode && (
            <SecondaryRow totalItems={totals.total} pro={pro} />
          )}
          {!hero && options.showGrid !== false && (
            // The module destroys the plugin's slider and forces a vertical
            // card list (js:1149-1170). Here the SAME results component stays
            // mounted in both layouts (a swap re-requested every full-price
            // quote, codex r4) and the sheet's CSS lays the slider out as a
            // vertical list instead.
            options.template === 'slider' ? sliderNode : gridNode
          )}
          {onSheet && <SheetHandle sheet={sheet} header={<SecondaryRow totalItems={totals.total} pro={pro} />} />}
        </div>

        {/* Restore control for the hidden map (#928): must live outside the map
            component, which unmounts on hide. Desktop-only via CSS; mobile keeps
            the results-header text toggle. */}
        {!onSheet && !hero && options.showMap && !state.mapVisible && <MapShowTab containerRef={containerRef} />}
        {onSheet && sheet.showToggle && <SheetFab sheet={sheet} />}

        {/* Compare Homes (#1713): pill, tray and dialog. Null unless compare is on. */}
        <CompareDock />
      </div>
    </MobileResultsContext.Provider>
  )
}
